import ScrollTable from "../ui/ScrollableTable";
import React, {useEffect, useMemo, useState} from "react";
import {BidBaseModel, Plug, PlugKind, PlugsView} from "../bid/bidding_model";
import {SovLine} from "../project_conf/bidConfig_model";
import {DevLineTag, PlugAddButton, PlugPopover} from "./PlugCell";
import {
    addDevLine, addPlug, BASE, cellKey, deleteDevLine, DEV_LINE, hasAmount, indexPlugs, MARK_TAG, kindLabel, kindStyle, parseCellValue, setDeveloperCell,
    setDevLineLabel, setDevLineType, signedMoney, STALE_PLUG, STALE_TITLE
} from "./plugs";
import {fetchWithToken, postWithToken} from "../../api/apiBase";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {dateFormatter, formatDate, moneyFormatter} from "../../lib/formatters";
import {MultiSelectCombobox} from "../ui/comboBox";
import { Button } from "../ui/button";
import { CopyButton2 } from "../exportData/CopyButton";
import { copyTableWihoutHeaderColumnToClipboard } from "../project_conf/datasheetHelpers";
import {useToast} from "../ui/use-toast";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../ui/tooltip";
import {ArrowRightLeft, MoreHorizontal} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";


enum CellType {
    dollar = "$",
    number = "num",
    date = "date",
    text = "txt",
}


type FieldCell = {
    value: any;
    type: CellType;
}

// plugs on one SOV cell with the same kind + note, across bids - one line under the row in the copy
type PlugLine = {
    kind: PlugKind;
    note: string;
    // bid id -> amount. summed if a bid has more than one plug in the line
    amounts: Record<string, number>;
}

type CompRow = {
    labels: string[];
    values: Record<string, FieldCell>;
    min?: number;
    max?: number;
    // SOV input cell id - rows from the server (and their plug lines). see server SovCompareRow
    cell_id?: number;
    // the cell's type - every bid's value on the row takes it
    type?: CellType | null;
    // the SOV cell the row's plugs are on. BASE on the base bid row, unset on the info rows
    plugKey?: string;
    plugLine?: PlugLine;
    // a developer only line (from the server). the developer types each bid's value
    developer_only?: boolean;
}


// something being dragged onto another cell - a bidder's number or a plug
type DragSource = {
    bid: BidBaseModel;
    amount: number;
    from: "value" | "plug";
    // the line it came from - its label, and its cell id (undefined for base bid plugs)
    label: string;
    cell_id?: number;
    note?: string;
    // a plug's kind - included / excluded carry as they are
    kind?: PlugKind;
}

type CompareBidsProps = {
    bids: BidBaseModel[];
    config: any;
    rows: CompRow[];
    // developer only - keyed by bid id
    plugs?: Record<string, Plug[]>;
    leveled?: Record<string, number>;
    // refetch - a plug was converted to an SOV line, so the rows changed
    onReload?: () => void;
}



function BidCompHeader(key: string, bids: BidBaseModel[], type: string, label:string){
    return {
        labels: [label],
        values: Object.fromEntries(
            // @ts-ignore
            bids.map((bid) => [bid.id, {value: bid[key], type:type}])
        ),
        min: Math.min(...bids.map(x=>x.bid_amount)),
        max: Math.max(...bids.map(x=>x.bid_amount))
    }
}



function FormattedCell(cell: FieldCell, orig: any){
    // text in a $ / num cell ('N/A', 'By MC' - kept from v1) shows as typed
    if ((cell.type === CellType.dollar || cell.type === CellType.number) && cell.value && isNaN(Number(cell.value))) {
        return <>{cell.value}</>
    }
    if (cell.type === CellType.dollar ){
        if(!cell.value){
            return <></>
        }
        var hue = Math.floor((1 - (cell.value- orig.min) / (orig.max - orig.min)) * 100);
        return <span style={{background:"hsl("+ hue+",40%,90%)"}} >
            {moneyFormatter.format(cell.value)}</span>

    } else if (cell.type === CellType.number) {
         if(!cell.value){
            return <></>
        }
        var hue = Math.floor((1 - (cell.value- orig.min) / (orig.max - orig.min)) * 100);
        return <span style={{background:"hsl("+ hue+",40%,90%)"}} >
            {cell.value}</span>

    }else if (cell.type === CellType.date) {
        return <>{formatDate(cell.value) }</>

    }else if (cell.type === CellType.text) {
        return <>{cell.value}</>
    }
    return <></>
}

// one money value per bid - plugs / leveled rows
function perBidRow(label: string, bids: BidBaseModel[], value: (bid: BidBaseModel) => number): CompRow {
    const values = bids.map(value);
    return {
        labels: [label],
        values: Object.fromEntries(bids.map((bid, k) => [bid.id, {value: values[k], type: CellType.dollar}])),
        min: Math.min(...values),
        max: Math.max(...values)
    }
}

// text for the copy button - same formatting as FormattedCell
function exportCell(cell?: FieldCell): string {
    if (!cell || cell.value === null || cell.value === undefined || cell.value === "") {
        return "";
    }
    if (cell.type === CellType.dollar && !isNaN(Number(cell.value))) {
        return cell.value ? moneyFormatter.format(cell.value) : "";
    }
    if (cell.type === CellType.date) {
        return String(formatDate(cell.value) || "");
    }
    return String(cell.value).replace(/\n/g, " ");
}

// plugs go on money / number lines (server checks the cell is an input) and the base bid
// text lines too - plugs there are always INFO (plug_service.is_text_cell)
function canPlug(row: CompRow, bid_id: string) {
    const type = row.values[bid_id]?.type;
    return row.plugKey === BASE || type === CellType.dollar || type === CellType.number || type === CellType.text;
}

// the plug lines under one row in the copy: plugs on its cell across all bids, grouped by kind + note (first seen first)
function plugLineRows(row: CompRow, bids: BidBaseModel[], plugIdx: Record<string, Record<string, Plug[]>>): CompRow[] {
    const key = row.plugKey;
    if (!key) {
        return [];
    }
    const lines: Record<string, CompRow> = {};
    const order: string[] = [];
    for (const bid of bids) {
        for (const p of plugIdx[bid.id]?.[key] || []) {
            const kind = p.kind ?? "add";
            const group = kind + "|" + (p.note || "").trim().toLowerCase();
            if (!lines[group]) {
                // the row's values ride along so the popover can show the sub's number
                lines[group] = {labels: [], values: row.values, cell_id: row.cell_id, plugKey: key,
                                plugLine: {kind, note: p.note, amounts: {}}};
                order.push(group);
            }
            const amounts = (lines[group].plugLine as PlugLine).amounts;
            amounts[bid.id] = (amounts[bid.id] ?? 0) + p.amount;
        }
    }
    return order.map((group) => lines[group]);
}

function HeaderCell(bid: BidBaseModel){
    // centered like the values under it. a real link - opens in a new tab too
    return <div className="text-center">
        <Link className="text-wrap text-xs hover:underline"
              to={`/project/${bid.project_id}/bid/${bid.id}`}>
            {bid.company_name + " Bid #" + String(bid.number)}
        </Link>
    </div>
}


// arrow key -> [rows, columns] to move
const ARROWS: Record<string, [number, number]> = {
    ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1]
};

/**
 * Keys in compare. arrows move around like a spreadsheet - every cell takes focus (ScrollTable focusCells).
 * a cell with an input focuses the input. in an input, left / right move the caret until it's at
 * the start / end of the text. leaving an input saves it (blur). a select keeps its own arrow keys.
 * enter on a cell with a plug "+" opens its plugs - esc closes them (the popover's own), back to the "+"
 * */
function cellKeys(e: React.KeyboardEvent) {
    const el = e.target as HTMLElement;
    // a plug popover / dialog is portaled out of the table - no td
    const td = el.closest("td");
    const tr = td?.parentElement as HTMLTableRowElement | null | undefined;
    if (!td || !tr) return;
    if (e.key === "Enter" && el === td) {
        const opener = td.querySelector<HTMLElement>("[data-opens-on-enter]");
        if (opener) {
            e.preventDefault();
            opener.click();
        }
        return;
    }
    const move = ARROWS[e.key];
    if (!move || el instanceof HTMLSelectElement) return;
    if (el instanceof HTMLInputElement && move[1] !== 0) {
        const atEdge = move[1] < 0
            ? el.selectionStart === 0 && el.selectionEnd === 0
            : el.selectionStart === el.value.length;
        if (!atEdge) return;
    }
    const row = move[0] < 0 ? tr.previousElementSibling : move[0] > 0 ? tr.nextElementSibling : tr;
    const cell = (row as HTMLTableRowElement | null)?.cells[td.cellIndex + move[1]];
    if (!cell) return;
    e.preventDefault();
    (cell.querySelector("input") ?? cell).focus();
}


/**
 * The developer's own number on a developer only line. Shows formatted, edits raw,
 * saves on blur / enter when it changed.
 * */
function DevCellInput({value, type, onCommit}: {
    value: any,
    type: CellType,
    onCommit: (value: number | string | null) => void
}) {
    const [text, setText] = useState(value == null ? "" : String(value));
    const [focused, setFocused] = useState(false);

    // a save (or new compare data) changed it
    useEffect(() => {
        if (!focused) setText(value == null ? "" : String(value));
    }, [value, focused]);

    const shown = focused || text === "" || type !== CellType.dollar
        ? text
        : moneyFormatter.format(parseCellValue(text) ?? 0);

    // text lines keep what was typed, the others take a number
    const parse = (v: any) => type === CellType.text ? (String(v ?? "").trim() || null) : parseCellValue(v);

    const commit = () => {
        setFocused(false);
        const next = parse(text);
        if (next !== parse(value)) onCommit(next);
    };

    return <input className={(type === CellType.text ? "w-full" : "w-28") + " rounded border px-1 text-center " +
                      DEV_LINE.input}
                  value={shown}
                  aria-label="Developer value"
                  onFocus={() => setFocused(true)}
                  onChange={(e) => setText(e.target.value)}
                  onBlur={commit}
                  onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}/>
}


/**
 * The title of a developer only line - renames it in the template (and every bid). saves on blur / enter.
 * */
function DevLabelInput({value, onCommit}: { value: string, onCommit: (label: string) => void }) {
    const [text, setText] = useState(value);

    useEffect(() => {
        setText(value);
    }, [value]);

    const commit = () => {
        if (text.trim() !== value) onCommit(text.trim());
    };

    return <input className={"min-w-0 flex-1 rounded border px-1 " + DEV_LINE.input}
                  value={text}
                  placeholder="Line name"
                  aria-label="Line name"
                  onChange={(e) => setText(e.target.value)}
                  onBlur={commit}
                  onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}/>
}


/**
 * The "..." menu on a developer only line - its type (dollar counts toward leveled, text doesn't) and delete.
 * rarely used, so it's in a menu - delete last, on its own, and it still asks first
 * */
function DevLineMenu({type, onType, onDelete}: {
    type: CellType,
    onType: (type: CellType) => void,
    onDelete: () => void
}) {
    // not modal - delete opens a dialog as the menu closes
    return <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Line actions" title="Line actions"
                    className="rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                <MoreHorizontal className="w-3 h-3"/>
            </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem checked={type === CellType.text}
                                      onCheckedChange={() => onType(CellType.text)}>
                Text
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={type === CellType.dollar}
                                      onCheckedChange={() => onType(CellType.dollar)}>
                Dollar
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator/>
            <DropdownMenuItem className="text-red-600" onSelect={onDelete}>
                Delete line
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
}


function bidToDropdown(bid: BidBaseModel){
    return {
        value: (bid as any).id ,
        label: `#${bid.number} - ${bid.company_name} ${moneyFormatter.format(bid.bid_amount)}`
    }
}

/**
 *
 *
 * */
function CompareBidsView({config, bids, rows, plugs: initialPlugs, leveled: initialLeveled, onReload}: CompareBidsProps){
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [colOrder, setColOrder] = useState([]);
    const [plugs, setPlugs] = useState<Record<string, Plug[]>>(initialPlugs || {});
    const [leveled, setLeveled] = useState<Record<string, number>>(initialLeveled || {});
    // developer only line values typed here since the data loaded - "bid id|cell key" -> value
    const [devValues, setDevValues] = useState<Record<string, number | string | null>>({});
    // developer only lines renamed here since the data loaded - cell id -> title
    const [devLabels, setDevLabels] = useState<Record<number, string>>({});

    // new compare data - different bids picked
    useEffect(() => {
        setPlugs(initialPlugs || {});
        setLeveled(initialLeveled || {});
    }, [initialPlugs, initialLeveled]);

    useEffect(() => {
        setDevValues({});
        setDevLabels({});
    }, [rows]);

    // bid id -> cell key -> plugs
    const plugIdx = useMemo(
        () => Object.fromEntries(bids.map((bid) => [bid.id, indexPlugs(plugs[bid.id])])),
        [bids, plugs]);

    // columns go lowest to highest leveled amount. sorted when the bids are picked and by the re-sort button -
    // not as plugs change it, so a column doesn't jump away while it is being leveled
    const byLeveled = (lv: Record<string, number>) =>
        [...bids].sort((a, b) => (lv[a.id] ?? a.bid_amount) - (lv[b.id] ?? b.bid_amount)).map((b) => b.id);
    const [order, setOrder] = useState<string[]>(() => byLeveled(initialLeveled || {}));

    // different bids picked - sort them. a reload of the same bids (eg convert to line) keeps the order
    useEffect(() => {
        setOrder((prev) => prev.length === bids.length && bids.every((b) => prev.includes(b.id))
            ? prev
            : byLeveled(initialLeveled || {}));
    }, [bids, initialLeveled]);

    const sortedBids = useMemo(
        () => [...bids].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)),
        [bids, order]);

    const onPlugsChanged = (bid_id: string, view: PlugsView) => {
        setPlugs((prev) => ({...prev, [bid_id]: view.plugs}));
        setLeveled((prev) => ({...prev, [bid_id]: view.leveled_amount}));
    }

    // a developer only line value - typed here, or as loaded
    const devKey = (bid: BidBaseModel, r: CompRow) => bid.id + "|" + r.plugKey;
    const devValue = (bid: BidBaseModel, r: CompRow) => {
        const k = devKey(bid, r);
        return k in devValues ? devValues[k] : r.values[bid.id]?.value;
    }

    // a developer only line's title - renamed here, or as loaded
    const devLabel = (r: CompRow) => {
        const cell = r.cell_id as number;
        return cell in devLabels ? devLabels[cell] : (r.labels[0] ?? "");
    }

    // the SOV line a row's cell is on - config is the SOV (server SovConfig)
    const lineIndex = (r: CompRow) =>
        (config.lines as SovLine[]).findIndex((line) => line.cells.some((c) => c.id === r.cell_id));

    const onDevLabel = (r: CompRow, label: string) => {
        setDevLabels((prev) => ({...prev, [r.cell_id as number]: label}));
        // the line's name is its first cell
        const first = config.lines[lineIndex(r)].cells[0].id;
        setDevLineLabel(config.project_id, config.id, first, label, accessToken as string)
            .catch((err) => {
                toast({title: "Error", description: "Line not renamed: " + err.message, variant: "destructive"});
            });
    }

    // a developer only line's type - every bid's cell has the template's type. no type is $
    const devType = (r: CompRow) => (Object.values(r.values)[0]?.type || CellType.dollar) as CellType;

    // changes the template, and the leveled amounts - compare is reloaded
    const onDevType = (r: CompRow, type: CellType) => {
        // picked the type it already is
        if (type === devType(r)) return;
        setDevLineType(config.project_id, config.id, r.cell_id as number, type, accessToken as string)
            .then(() => onReload && onReload())
            .catch((err) => {
                toast({title: "Error", description: "Type not changed: " + err.message, variant: "destructive"});
            });
    }

    // the developer only line waiting on the delete dialog
    const [deleting, setDeleting] = useState<CompRow | null>(null);

    // the whole line goes, with its values on every bid - compare is reloaded
    const onDeleteDevLine = (r: CompRow) => {
        deleteDevLine(config.project_id, config.id, lineIndex(r), accessToken as string)
            .then(() => onReload && onReload())
            .catch((err) => {
                toast({title: "Error", description: "Line not deleted: " + err.message, variant: "destructive"});
            });
    }

    // a blank developer only line under this one - the SOV changed, so compare is reloaded
    const onAddDevLine = (r: CompRow) => {
        addDevLine(config.project_id, config.id, r.cell_id as number, accessToken as string)
            .then(() => onReload && onReload())
            .catch((err) => {
                toast({title: "Error", description: "Line not added: " + err.message, variant: "destructive"});
            });
    }

    const onDevCell = (bid: BidBaseModel, r: CompRow, value: number | string | null) => {
        setDevValues((prev) => ({...prev, [devKey(bid, r)]: value}));
        setDeveloperCell(bid.project_id, bid.id, r.cell_id as number, value, accessToken as string)
            .then((view) => onPlugsChanged(bid.id, view))
            .catch((err) => {
                toast({title: "Error", description: "Not saved: " + String(err.message), variant: "destructive"});
            });
    }

    // ---- drag a number or a plug onto another cell ----
    // onto another bidder's plug-able cell -> an OSV plug. onto a developer only cell -> sets its value
    const [drag, setDrag] = useState<DragSource | null>(null);
    // the cell being dragged over - "bid id|cell key"
    const [dropKey, setDropKey] = useState<string | null>(null);

    const startDrag = (e: React.DragEvent, source: DragSource) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = "copy";
        // firefox only drags with some data set
        e.dataTransfer.setData("text/plain", String(source.amount));
        setDrag(source);
    }

    const endDrag = () => {
        setDrag(null);
        setDropKey(null);
    }

    // handlers for a cell that takes drops - nothing when it can't take this drag
    const dropProps = (key: string, ok: boolean, onDrop: (source: DragSource) => void) => {
        if (!drag || !ok) return {};
        const source = drag;
        return {
            onDragOver: (e: React.DragEvent) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
                if (dropKey !== key) setDropKey(key);
            },
            onDragLeave: () => setDropKey((k) => k === key ? null : k),
            onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                onDrop(source);
                endDrag();
            },
        };
    }

    // an OSV - other sub's value. a number levels the line to that number (the plug is the difference
    // from this bidder's own number), a plug is copied as it is - whatever its amount, 0 too.
    // onto a text line it's an INFO note (plug_service.is_text_cell). included / excluded stay what they are
    const dropOsv = (bid: BidBaseModel, r: CompRow, source: DragSource) => {
        const text = r.values[bid.id]?.type === CellType.text;
        const mark = !!source.kind && !hasAmount(source.kind);
        const own = r.values[bid.id]?.value;
        const amount = source.from === "value"
            ? Math.round(source.amount - (typeof own === "number" ? own : 0))
            : source.amount;
        if (source.from === "value" && !amount && !text) {
            toast({title: "Nothing to carry", description: "That's already this bidder's number."});
            return;
        }
        const kind: PlugKind = text ? "info" : mark ? source.kind as PlugKind : "osv";
        const sameLine = source.cell_id === r.cell_id;
        // INFO notes and included / excluded are carried as they are - no "From ..." in front
        const note = text || mark
            ? (source.note ?? "")
            : "From " + source.bid.company_name + (sameLine ? "" : ", " + source.label) +
                (source.from === "value"
                    ? " (" + moneyFormatter.format(source.amount) + ")"
                    : (source.note ? ": " + source.note : ""));
        addPlug(bid.project_id, bid.id, r.cell_id as number, {amount, note, kind}, accessToken as string)
            .then((view) => onPlugsChanged(bid.id, view))
            .catch((err) => {
                toast({title: "Error", description: "Plug not added: " + err.message, variant: "destructive"});
            });
    }

    // drop handlers + ring for one bid's cell (td) on one row - the whole cell takes the drop.
    // an OSV onto another bidder's line, a value onto a developer only cell (not a developer text cell -
    // no numbers there). a bidder's text cell takes drops as INFO notes
    const cellDrop = (bid: BidBaseModel, r: CompRow): React.TdHTMLAttributes<HTMLTableCellElement> => {
        if (!r.values[bid.id] || !r.plugKey || (!r.developer_only && !canPlug(r, bid.id))) return {};
        const key = bid.id + "|" + r.plugKey;
        const sovLine = r.cell_id != null && r.plugKey !== BASE;
        const text = r.values[bid.id].type === CellType.text;
        const drop = r.developer_only
            ? dropProps(key, sovLine && !text, (source) => onDevCell(bid, r, source.amount))
            : dropProps(key, sovLine && !!drag && drag.bid.id !== bid.id, (source) => dropOsv(bid, r, source));
        const ring = dropKey === key
            ? "ring-2 ring-inset " + (r.developer_only ? "ring-violet-400" : "ring-[#c9a27e]")
            : "";
        return {...drop, className: ring};
    }

    // the plug popover for one bid on one row - same as the bid page SOV.
    // convert to line changes the SOV, so compare is reloaded
    const plugPopover = (bid: BidBaseModel, r: CompRow, trigger: React.ReactElement) => {
        const key = r.plugKey as string;
        const sub = key === BASE ? bid.bid_amount : r.values[bid.id]?.value;
        return <PlugPopover
            project_id={bid.project_id}
            bid_id={bid.id}
            cell_id={key === BASE ? null : r.cell_id as number}
            plugs={plugIdx[bid.id][key] || []}
            subValue={typeof sub === "number" ? sub : null}
            onChange={(view) => onPlugsChanged(bid.id, view)}
            onConverted={onReload}
            infoOnly={key !== BASE && r.values[bid.id]?.type === CellType.text}>
            {trigger}
        </PlugPopover>
    }

    // exportHeader / exportValue are what the copy button puts on the clipboard
    const buildCols = () => {
        let cols: any = [];
        // gutter on the left - "+" on each SOV line adds a developer only line under it. not in the copy
        cols.push({
            key: "add",
            header: "",
            className: "w-6",
            cell: (row: any) => {
                const r: CompRow = row.original;
                return r.cell_id != null
                    ? <PlugAddButton aria-label="Add developer line"
                                     title="Add a developer only line under this one"
                                     onClick={() => onAddDevLine(r)}/>
                    : null;
            }
        })
        // definition columns. at least one - the info / plug rows label there
        const num_label_cols = Math.max(1, ...rows.map(row => row.labels.length));

        for (let i = 0; i < num_label_cols; i++) {
            cols.push({
                key: i,
                header: "",
                exportHeader: "",
                exportValue: (row: CompRow) => row.plugLine
                    ? (i === 0 ? kindLabel(row.plugLine.kind) + " " + row.plugLine.note : "")
                    : row.developer_only && i === 0
                        ? devLabel(row)
                        : row.labels[i] ?? "",
                cell: (row: any) => {
                    const r: CompRow = row.original;
                    if (r.developer_only && i === 0) {
                        return <div className="flex items-center gap-1">
                            <DevLineTag/>
                            <DevLabelInput value={devLabel(r)} onCommit={(label) => onDevLabel(r, label)}/>
                            <DevLineMenu type={devType(r)}
                                         onType={(type) => onDevType(r, type)}
                                         onDelete={() => setDeleting(r)}/>
                        </div>
                    }
                    if(r.labels.length > i){
                        return <div>{r.labels[i]}</div>
                    }
                }
            })
        }

        // input columns
        for (let i = 0; i < sortedBids.length; i++) {
            let bid = sortedBids[i]
            const def = {
                key: bid.id,
                // grey line between bidders (and between the labels and the first bidder)
                className: "border-l border-gray-300",
                header: HeaderCell(bid),
                exportHeader: bid.company_name + " Bid #" + String(bid.number),
                // a plug line's amount for this bid - included / excluded have none, so the kind marks which bids
                exportValue: (row: CompRow) => row.plugLine
                    ? (bid.id in row.plugLine.amounts
                        ? (hasAmount(row.plugLine.kind)
                            ? String(row.plugLine.amounts[bid.id])
                            : kindLabel(row.plugLine.kind))
                        : "")
                    : row.developer_only && row.values[bid.id]
                        ? exportCell({...row.values[bid.id], value: devValue(bid, row)})
                        : exportCell(row.values[bid.id]),
                // the whole cell takes drops (cellDrop)
                tdProps: (row: any) => cellDrop(bid, row.original),
                cell: (row: any) => {
                    const r: CompRow = row.original;
                    if (!r.values[bid.id]){
                        return <div>N/A</div>
                    }
                    const value = FormattedCell(r.values[bid.id], r);
                    // developer only lines always go on - the developer types in them, whatever their type
                    if (!r.plugKey || (!r.developer_only && !canPlug(r, bid.id))) {
                        // notes on SOV lines are smaller than the numbers
                        const note = r.cell_id != null && r.values[bid.id].type === CellType.text;
                        return <div className={"text-center" + (note ? " text-[10px] leading-tight" : "")}>{value}</div>;
                    }
                    const cellPlugs = plugIdx[bid.id][r.plugKey] || [];
                    const label = r.plugKey === BASE ? "base bid" : (r.labels[0] ?? "");
                    const sovLine = r.cell_id != null && r.plugKey !== BASE;
                    const text = r.values[bid.id].type === CellType.text;

                    // the bidder's own number on an SOV line can be dragged to another bidder.
                    // the bidder's text shows as it is
                    const own = r.values[bid.id].value;
                    const shown = !text && sovLine && !r.developer_only && typeof own === "number" && own
                        ? <span draggable className="cursor-grab"
                                title="Drag onto another bidder's line to carry it as an OSV"
                                onDragStart={(e) => startDrag(e, {bid, amount: own, from: "value", label, cell_id: r.cell_id})}
                                onDragEnd={endDrag}>
                            {value}
                        </span>
                        : value;

                    // "+" adds a plug. kept out of the flow, with room saved for it on the right (pr-5)
                    // so it never sits on the number - the number centers in what's left
                    return <div>
                        <div className={"relative flex min-h-[20px] items-center justify-center" +
                            (r.developer_only ? "" : " pr-5") +
                            // notes on SOV lines are smaller than the numbers
                            (text && !r.developer_only ? " text-[10px] leading-tight" : "")}>
                            {r.developer_only
                                ? <DevCellInput value={devValue(bid, r)}
                                                type={r.values[bid.id].type}
                                                onCommit={(v) => onDevCell(bid, r, v)}/>
                                : shown}
                            {/* no plugs on developer only lines - the amount is typed in */}
                            {!r.developer_only &&
                                <span className="absolute right-0">
                                    {/* enter on the cell clicks it (cellKeys) */}
                                    {plugPopover(bid, r, <PlugAddButton data-opens-on-enter=""/>)}
                                </span>}
                        </div>
                        {/* the cell's plugs under its value - kind amount | note. click to edit, drag to carry.
                            a div, not a button - firefox won't drag things inside a button */}
                        {cellPlugs.length > 0 && plugPopover(bid, r,
                            <div role="button" tabIndex={0}
                                 className="w-full mt-1 text-[10px] font-normal cursor-pointer hover:opacity-80"
                                 onKeyDown={(e) => (e.key === "Enter" || e.key === " ") &&
                                     (e.currentTarget as HTMLElement).click()}>
                                {cellPlugs.map((p) =>
                                    <span key={p.id} draggable
                                          title={(p.stale ? STALE_TITLE + ". " : "") +
                                              kindLabel(p.kind ?? "add") + " " + (p.note || "") +
                                              " - drag onto another bidder's line to carry it"}
                                          onDragStart={(e) => startDrag(e, {
                                              bid, amount: p.amount, from: "plug", label,
                                              cell_id: r.plugKey === BASE ? undefined : r.cell_id, note: p.note, kind: p.kind
                                          })}
                                          onDragEnd={endDrag}
                                          className={(hasAmount(p.kind)
                                              ? "flex flex-wrap items-center gap-x-2 rounded px-1 cursor-grab " +
                                                  kindStyle(p.kind).line
                                              // included / excluded - a larger tag in the middle, the note under it
                                              : "flex flex-col items-center gap-0.5 rounded px-1 py-0.5 cursor-grab") +
                                              (p.stale ? " " + STALE_PLUG : "")}>
                                        {hasAmount(p.kind)
                                            ? <>
                                                {/* kind + amount never split. the note sits beside them,
                                                    or wraps to its own line when the column is narrow */}
                                                <span className="flex shrink-0 gap-1 items-center whitespace-nowrap">
                                                    <span className={"rounded px-1 text-[9px] font-medium " +
                                                        kindStyle(p.kind).tag}>
                                                        {kindLabel(p.kind ?? "add")}
                                                    </span>
                                                    {signedMoney(p.amount)}
                                                </span>
                                                <span className="min-w-0 flex-1 basis-24 text-left break-words">
                                                    {p.note || <i className="text-gray-400">no note</i>}
                                                </span>
                                            </>
                                            : <>
                                                <span className={MARK_TAG + " " + kindStyle(p.kind).tag}>
                                                    {kindLabel(p.kind)}
                                                </span>
                                                {p.note && <span className="text-center break-words">{p.note}</span>}
                                            </>}
                                    </span>)}
                            </div>)}
                    </div>
                }
            }
            cols.push(def)
        }
        // setColumns(cols)
        return cols;
    }

    // plugs show in their cell on screen. the copy has them as lines under the row, so numbers stay in cells
    const buildRows = (withPlugLines: boolean): CompRow[] => {
        const lines = (r: CompRow) => withPlugLines ? plugLineRows(r, bids, plugIdx) : [];
        const base = {...BidCompHeader('bid_amount', bids, "$" , "Base bid"), plugKey: BASE} as CompRow;
        return [

            BidCompHeader('company_name', bids, "txt" , "Company"),
            base,
            ...lines(base),
            // everything between base bid and leveled - plugs and developer only lines - so the column adds up
            perBidRow("Adjustments", bids, (bid) => (leveled[bid.id] ?? bid.bid_amount) - bid.bid_amount),
            perBidRow("Leveled", bids, (bid) => leveled[bid.id] ?? bid.bid_amount),
            BidCompHeader('submitted_at', bids, "date", "Submitted"),
            BidCompHeader('response_status', bids, "txt", "Response"),
            BidCompHeader(' ', bids, "", ""),
            // each SOV row, then its plug lines
            ...rows.flatMap((row) => {
                // each bid's value takes the row's type (no type is $) - so it formats like the rows above
                const type = row.type || CellType.dollar;
                const values = Object.fromEntries(Object.entries(row.values).map(([id, v]) => [id, {...v, type}]));
                const r: CompRow = {...row, values, plugKey: cellKey(row.cell_id)};
                return [r, ...lines(r)];
            })
        ] as CompRow[]
    }

    const formatData = () => {
        // columns with something to copy - not the "+" gutter
        const cols = buildCols().filter((col: any) => col.exportValue)
        return [
            cols.map((col: any) => col.exportHeader),
            ...buildRows(true).map((row) => cols.map((col: any) => col.exportValue(row)))
        ];
    }
    return <>
        <div className="max-w-full text-xs relative" onKeyDown={cellKeys}>
            <div className="absolute -top-11 right-20 z-10 flex gap-2">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button type="button" variant="outline" onClick={() => setOrder(byLeveled(leveled))}>
                                <ArrowRightLeft width={17} height={17}/>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Sort bids by leveled amount</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
        <CopyButton2 dataTransform={formatData} executeCustomCopy={copyTableWihoutHeaderColumnToClipboard}/>
            </div>
            <ScrollTable
                columns={buildCols()}
                data={buildRows(false) }
                // tighter cells than the table default (py-2 px-4).
                // standard rows (base bid, leveled ...) are bold - SOV rows have a cell id
                rowClassName={(row: CompRow) => "[&>td]:py-1 [&>td]:px-2 " + (row.cell_id != null ? "" : "font-bold") +
                    (row.developer_only ? " " + DEV_LINE.row : "")}
                // every cell takes focus - arrow keys move between them (cellKeys)
                focusCells
                maxHeight=""/>
        </div>
        <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete this line</DialogTitle>
                    <DialogDescription>
                        The developer only line and its values on every bid will be deleted.
                        This can't be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="button" variant="destructive" onClick={() => {
                        if (deleting !== null) onDeleteDevLine(deleting);
                        setDeleting(null);
                    }}>
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>
}




/**
 * /project/{project_id}/bidding/compare
 * */
function CompareBids(){
    const navigate = useNavigate();
    const {state} = useLocation();
    const {project_id} = useParams();
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [data, setData] = useState<CompareBidsProps | null>(null);
    const [bids, setBids] = useState<{value:string, label:string}[]>([]);
    const [selectedBids, setSelectedBids] = useState([]);
    const [isStateLoaded, setIsStateLoaded] = useState(false);
    const [isFromMultiSelect, setIsFromMultiSelect] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(()=> {
        if (state && state.ids) {
            setSelectedBids(state.ids);
        }
            setIsStateLoaded(true)
    }, [state])

    useEffect(() => {
        fetchWithToken(
            `/projects/${project_id}/bidding/`,
            accessToken as string)
            .then((data: BidBaseModel[]) => {
                setBids(data.map(bid => bidToDropdown(bid)))

            })
            .catch((err) => {
                console.log('err on get bids', err);
            })
    }, [project_id])

    useEffect(() => {
        if (!isStateLoaded) return
        let query = null
        if(selectedBids.length === 0){
            query = {latest: true}
        } else {
            query = {ids: selectedBids, latest: false}
        }
        // console.log('query', query)
        postWithToken(`/projects/${project_id}/developer/compare`,
            JSON.stringify(query),
            accessToken as string)
            .then((data) => {
                // console.log(data, 'data');
                setData(data)
                // @ts-ignore
                setSelectedBids(data.bids.map(bid => bid.id))
            })
            .catch((err) => {
                toast({
                        title: "Error",
                        duration: 5000,
                        description: "Failed to load: " + JSON.stringify(err),
                        variant: "destructive",
                    })
            })
    }, [project_id, isStateLoaded, isFromMultiSelect])

    const handleValueChange = (dx:any) => {
        console.log(dx)
        setSelectedBids(dx)
        setIsFromMultiSelect((prev) => !prev)
    }

    if(!data){ return <></>}
    if(!bids){ return <></>}

    return <div >
        <div className="w-full flex justify-between mb-2">
            <div className="">
        <MultiSelectCombobox
              width="w-[250px]"
              types={  bids }
              open={isOpen}
              setOpen={() => setIsOpen((value) => !value)}
              value={ selectedBids}
              displayValue="select more"
              onChange={(value) => {
                  handleValueChange(value);
              }}
            />
                </div>

        </div>
        <CompareBidsView
            bids={data.bids}
            config={data.config}
            rows={data.rows}
            plugs={data.plugs}
            leveled={data.leveled}
            onReload={() => setIsFromMultiSelect((prev) => !prev)}
        />
    </div>
}


export default CompareBids


type CompareBidsInlineProps = {
    selectedRows: string[];
    bids: BidBaseModel[];
    // a plug was converted to an SOV line - the template changed
    onSovChanged?: () => void;
}

export function CompareBidsInline({selectedRows, bids, onSovChanged}: CompareBidsInlineProps){
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const {project_id, scope_id} = useParams<{  project_id: string, scope_id: string }>();
    const [data, setData] = useState<CompareBidsProps | null>(null);

    const dropdownBids = bids.map(bid => bidToDropdown(bid))
    const [selectedBids, setSelectedBids] = useState<string[]>(selectedRows || []);
    const [isStateLoaded, setIsStateLoaded] = useState(false);
    const [isFromMultiSelect, setIsFromMultiSelect] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setSelectedBids(selectedRows || []);
        setIsStateLoaded(true);
    }, [selectedRows]);

    useEffect(() => {
        if (!isStateLoaded) return;

        // nothing selected -> most recent submitted bid per company
        const ids = selectedBids.filter(Boolean);
        let query = ids.length === 0
            ? {latest: true, scope_id: scope_id}
            : {ids: ids, latest: false, scope_id: scope_id};
        postWithToken(`/projects/${project_id}/developer/compare`,
            JSON.stringify(query),
            accessToken as string)
            .then((data) => {
                setData(data)
                setSelectedBids(data.bids.map((bid: any) => bid.id))
            })
            .catch((err) => {
                toast({
                    title: "Error",
                    duration: 5000,
                    description: "Failed to load: " + JSON.stringify(err),
                    variant: "destructive",
                })
            })
    }, [project_id, isStateLoaded, isFromMultiSelect])

    const handleValueChange = (dx: any) => {
        setSelectedBids(dx)
        setIsFromMultiSelect((prev) => !prev)
    }

    if(!data){ return <></>}
    if(!bids){ return <></>}

    return <div>
        <div className="w-full flex justify-between mb-2">
            <div className="">
                <MultiSelectCombobox
                    width="w-[250px]"
                    types={dropdownBids}
                    open={isOpen}
                    setOpen={() => setIsOpen((value) => !value)}
                    value={selectedBids}
                    displayValue="select more"
                    onChange={(value) => {
                        handleValueChange(value);
                    }}
                />
            </div>
        </div>
        <CompareBidsView
            bids={data.bids}
            config={data.config}
            rows={data.rows}
            plugs={data.plugs}
            leveled={data.leveled}
            onReload={() => {
                setIsFromMultiSelect((prev) => !prev);
                onSovChanged && onSovChanged();
            }}
        />
    </div>
}

